"use client";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { AccessibilityStatement } from "../../types/statement";
import { z } from "zod";
import { useState } from "react";
import { HelpText } from "../help-text";

export const basicInfoSchema = z.object({
  companyName: z.string().min(1, { message: "Company name is required" }),
  websiteUrl: z
    .string()
    .min(1, { message: "Website URL is required" })
    .url({ message: "Website URL must be a valid URL" }),
  websiteName: z.string().min(1, { message: "Website name is required" }),
});

interface BasicInformationStepProps {
  data: AccessibilityStatement["basicInfo"];
  onChange: (
    data: AccessibilityStatement["basicInfo"],
    changedField?: keyof AccessibilityStatement["basicInfo"]
  ) => void;
  externalErrors?: {
    companyName?: string;
    websiteUrl?: string;
    websiteName?: string;
  } | null;
}

export function BasicInformationStep({
  data,
  onChange,
  externalErrors,
}: BasicInformationStepProps) {
  const [errors, setErrors] = useState<{
    companyName?: string;
    websiteUrl?: string;
    websiteName?: string;
  }>({});

  const handleChange = (
    field: keyof AccessibilityStatement["basicInfo"],
    value: string
  ) => {
    const newData = { ...data, [field]: value };
    // Validate only the changed field
    if (field in basicInfoSchema.shape) {
      const fieldSchema = z.object({ [field]: basicInfoSchema.shape[field as keyof typeof basicInfoSchema.shape] });
      const result = fieldSchema.safeParse({ [field]: value });
      if (!result.success) {
        setErrors((prev) => ({
          ...prev,
          [field]: result.error.errors[0]?.message || "",
        }));
      } else {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    }
    onChange(newData, field);
  };

  // Prefer externalErrors if present, otherwise use local errors
  const displayError = (field: keyof typeof errors) => {
    return externalErrors?.[field] || errors[field];
  };

  return (
    <div className="cy:space-y-6">
      <div>
        <h2 className="cy:text-2xl cy:font-semibold cy:mb-2">
          Basic information
        </h2>
        <p className="cy:text-secondary-foreground! cy:text-base! cy:antialiased">
          To get started, tell us about your business and website. Fields with
          an asterisk (*) must be filled in.
        </p>
      </div>

      <div className="cy:space-y-6 cy:mt-10">
        <div>
          <Label
            htmlFor="companyName"
            className="cy:text-lg cy:font-semibold"
          >
            Organisation name*
          </Label>
          <HelpText>Example: Example Inc.</HelpText>
          <Input
            id="companyName"
            value={data.companyName}
            onChange={(e) => handleChange("companyName", e.target.value)}
            className="cy:mt-2"
            aria-invalid={!!displayError("companyName")}
            aria-describedby={
              displayError("companyName") ? "companyName-error" : undefined
            }
          />
          {displayError("companyName") && (
            <div
              id="companyName-error"
              className="cy:text-red-600 cy:text-sm cy:mt-1"
            >
              {displayError("companyName")}
            </div>
          )}
        </div>

        <div>
          <Label
            htmlFor="websiteUrl"
            className="cy:text-lg cy:font-semibold"
          >
            Website*
          </Label>
          <HelpText>Example: https://yourwebsite.com</HelpText>
          <Input
            id="websiteUrl"
            value={data.websiteUrl}
            onChange={(e) => handleChange("websiteUrl", e.target.value)}
            className="cy:mt-1"
            aria-invalid={!!displayError("websiteUrl")}
            aria-describedby={
              displayError("websiteUrl") ? "websiteUrl-error" : undefined
            }
          />
          {displayError("websiteUrl") && (
            <div
              id="websiteUrl-error"
              className="cy:text-red-600 cy:text-sm cy:mt-1"
            >
              {displayError("websiteUrl")}
            </div>
          )}
        </div>
        <div>
          <Label
            htmlFor="websiteName"
            className="cy:text-lg cy:font-semibold"
          >
            Website name*
          </Label>
          <HelpText>Example: Example Site</HelpText>
          <Input
            id="websiteName"
            value={data.websiteName}
            onChange={(e) => handleChange("websiteName", e.target.value)}
            className="cy:mt-1"
            aria-invalid={!!displayError("websiteName")}
            aria-describedby={
              displayError("websiteName") ? "websiteName-error" : undefined
            }
          />
          {displayError("websiteName") && (
            <div
              id="websiteName-error"
              className="cy:text-red-600 cy:text-sm cy:mt-1"
            >
              {displayError("websiteName")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
